/**
 * OUTFIT — Service API AWS
 * Appels vers API Gateway → Lambda → Aurora PostgreSQL
 * Fallback automatique en mode démo
 */

// v1.12.117 — wrapper avec fallback localStorage sur web
import * as SecureStore from '../utils/secureStore';
import { AWS_CONFIG, IS_AWS_CONFIGURED } from '../config/aws_config';
import { wardrobe as MOCK_WARDROBE, looks as MOCK_LOOKS, userProfile as MOCK_PROFILE } from '../data/index';
import { CATALOG } from '../data/catalogData';
import { hasUsableImage } from '../utils/imageFilter';

async function getCognitoToken() {
  try {
    // Clé sauvegardée par AuthContext.saveTokens()
    const token = await SecureStore.getItemAsync('outfit_access');
    if (token) return token;
    // Fallback ancien nom au cas où
    return await SecureStore.getItemAsync('outfit_cognito_access_token');
  } catch { return null; }
}

async function apiCall(method, endpoint, body, externalToken, opts = {}) {
  if (!IS_AWS_CONFIGURED) return null;
  // ═══ v1.7.67 : timeout côté client + retry sur erreur réseau ═══
  // Avant : fetch sans AbortController. Si l'API Gateway prenait >30s
  //   (Lambda cold start + RDS qui dort), l'app restait bloquée
  //   indéfiniment → écran figé pour l'user.
  // Maintenant :
  //   - timeout 25s par défaut (couvre cold start + retry pool DB côté Lambda)
  //   - retry automatique 1 fois sur les codes 502/503/504 et timeouts réseau
  //   - return null gracieux après échec total → l'écran reste utilisable
  //     avec son cache local au lieu de freeze.
  // v1.12.567 : opts.ifNoneMatch → envoi If-None-Match, opts.returnMeta → renvoie { data, etag, status }
  const token = externalToken || await getCognitoToken();
  const timeoutMs = opts.timeout || 25000;
  const maxRetries = opts.retries ?? 1;

  const tryOnce = async () => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res = await fetch(`${AWS_CONFIG.apiUrl}${endpoint}`, {
        method,
        signal: ctrl.signal,
        headers: {
          'Content-Type': 'application/json',
          'Accept-Encoding': 'gzip',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(opts.ifNoneMatch ? { 'If-None-Match': opts.ifNoneMatch } : {}),
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      clearTimeout(timer);
      // v1.12.567 — Support 304 Not Modified pour ETag cache
      if (res.status === 304) {
        if (opts.returnMeta) return { data: null, etag: res.headers.get('ETag') || opts.ifNoneMatch, status: 304 };
        return null;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const error = new Error(err.error || `HTTP ${res.status}`);
        error.status = res.status;
        throw error;
      }
      const data = await res.json();
      if (opts.returnMeta) return { data, etag: res.headers.get('ETag'), status: res.status };
      return data;
    } catch (e) {
      clearTimeout(timer);
      if (e.name === 'AbortError') {
        const tErr = new Error('Request timeout');
        tErr.isTimeout = true;
        throw tErr;
      }
      throw e;
    }
  };

  let lastErr;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await tryOnce();
    } catch (e) {
      lastErr = e;
      const retryable = e.isTimeout || e.status === 502 || e.status === 503 || e.status === 504
        || /network|fetch|Failed/i.test(e.message || '');
      if (!retryable || attempt === maxRetries) break;
      console.warn(`[apiCall] retry ${endpoint} after: ${e.message} (attempt ${attempt + 1}/${maxRetries})`);
      // Backoff progressif
      await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
    }
  }
  throw lastErr;
}

export async function getProfile(userId) {
  if (!IS_AWS_CONFIGURED) return MOCK_PROFILE;
  try { return await apiCall('GET', '/profile'); } catch { return MOCK_PROFILE; }
}

export async function updateProfile(userId, updates) {
  if (!IS_AWS_CONFIGURED) return { ...MOCK_PROFILE, ...updates };
  return apiCall('PUT', '/profile', updates);
}

export async function updatePushToken(userId, token) {
  if (!IS_AWS_CONFIGURED) return;
  await apiCall('PUT', '/profile', { push_token: token });
}

// v1.12.684 — Dédup des requêtes GET /wardrobe concurrentes.
// Si un render loop (useEffect mal câblé) déclenche getWardrobe en rafale,
// on retourne la promesse en cours au lieu de spammer l'API (→ ERR_FAILED /
// throttle CORS). Le résultat est aussi caché 2s pour absorber les bursts.
let _wardrobeInflight = null;
let _wardrobeCache = { key: null, data: null, at: 0 };
export async function getWardrobe(userId, token) {
  if (!IS_AWS_CONFIGURED) return MOCK_WARDROBE;
  const key = String(userId || 'anon');
  // Cache court (2s) : absorbe les bursts de re-render
  if (_wardrobeCache.key === key && _wardrobeCache.data && (Date.now() - _wardrobeCache.at) < 2000) {
    return _wardrobeCache.data;
  }
  // Requête déjà en vol pour ce user → réutiliser la promesse
  if (_wardrobeInflight && _wardrobeInflight.key === key) {
    return _wardrobeInflight.promise;
  }
  const promise = (async () => {
    const rows = await apiCall('GET', '/wardrobe', null, token);
    const mapped = !rows?.length ? [] : rows.map(item => ({
      ...item,
      image: item.image_url || item.image || null,
      emoji: item.emoji     || '👗',
    }));
    _wardrobeCache = { key, data: mapped, at: Date.now() };
    return mapped;
  })();
  _wardrobeInflight = { key, promise };
  try {
    return await promise;
  } finally {
    if (_wardrobeInflight && _wardrobeInflight.promise === promise) _wardrobeInflight = null;
  }
}

// v1.12.567 — Variante avec ETag : envoie If-None-Match, retourne { data, etag, status }
// Si status===304 → l'appelant doit utiliser son cache local
export async function getWardrobeWithEtag(userId, token, etag) {
  if (!IS_AWS_CONFIGURED) return { data: MOCK_WARDROBE, etag: null, status: 200 };
  const result = await apiCall('GET', '/wardrobe', null, token, { ifNoneMatch: etag, returnMeta: true });
  if (result.status === 304) return result;
  const rows = result.data || [];
  return {
    data: rows.map(item => ({
      ...item,
      image: item.image_url || item.image || null,
      emoji: item.emoji     || '👗',
    })),
    etag: result.etag,
    status: result.status,
  };
}

export async function addWardrobeItem(userId, item, token) {
  if (!IS_AWS_CONFIGURED) return { ...item, id: `w_${Date.now()}` };

  // ═══ FIX 2026-04 : garde-fou ai_metadata obligatoire ═══
  // Tout article ajouté au dressing doit avoir été analysé par l'IA d'abord.
  // Cette garantie permet aux Looks et au matching couleur/style de fonctionner
  // de façon fiable. Les chemins d'appel (WardrobeScreen, PurchaseConfirmModal)
  // doivent analyser AVANT d'appeler cette fonction. Si on arrive ici sans
  // metadata, c'est un bug → on rejette explicitement plutôt que de polluer
  // la DB avec un item incomplet.
  if (!item.ai_metadata || typeof item.ai_metadata !== 'object') {
    console.warn('[DatabaseService.addWardrobeItem] ⛔ refus : ai_metadata manquante');
    // v1.13.56 — GA : erreur à l'ajout au dressing
    try { require('./AnalyticsService').trackEvent('wardrobe_add_error', { reason: 'ai_metadata_required', source: 'save', category: item?.category || null }); } catch {}
    throw new Error('ai_metadata_required');
  }

  let result;
  try {
    result = await apiCall('POST', '/wardrobe', {
      name: item.name, category: item.category, color: item.color,
      style: item.style, brand: item.brand || '', emoji: item.emoji || '👗',
      image_url: item.image_url || null, ai_tip: item.ai_tip || null,
      ai_metadata: item.ai_metadata,
    }, token);
  } catch (e) {
    // v1.13.56 — GA : échec d'enregistrement backend (quota serveur, 5xx, réseau…)
    try { require('./AnalyticsService').trackEvent('wardrobe_add_error', { reason: 'save_failed', source: 'save', detail: (e?.message || '').slice(0, 100), category: item?.category || null }); } catch {}
    throw e;
  }
  // v1.12.684 — Invalider le cache wardrobe (sinon ancien dressing affiché)
  _wardrobeCache = { key: null, data: null, at: 0 };
  // v1.12.682 — GA4 : article ajouté au dressing
  try {
    const { trackEvent } = require('./AnalyticsService');
    trackEvent('wardrobe_item_added', {
      // v1.13.18 — source : 'dressing' = ajout photo manuel depuis le dressing.
      // (Le chemin catalogue « J'ai acheté » émet source:'catalog' via
      //  PurchaseTrackingService ; l'ajout par URL émet source:'url'.)
      source: item.source || 'dressing',
      category: item.category || 'unknown',
      color: item.color || null,
      style: item.style || null,
      brand: item.brand || null,
    });
  } catch {}
  return result;
}

export async function updateWardrobeItem(itemId, updates) {
  if (!IS_AWS_CONFIGURED) return { id: itemId, ...updates };
  return apiCall('PUT', `/wardrobe/${itemId}`, updates);
}

export async function deleteWardrobeItem(itemId) {
  if (!IS_AWS_CONFIGURED) return;
  await apiCall('DELETE', `/wardrobe/${itemId}`);
}

// Supprime l'article ET son image S3 si elle existe
export async function deleteWardrobeItemFull(item, token) {
  const itemId   = item.id;
  const imageUrl = item.image_url || item.image || null;

  // 1. Supprimer de la DB via Lambda (réponse immédiate)
  try {
    await apiCall('DELETE', `/wardrobe/${itemId}`, null, token);
  } catch(e) {
    console.warn('deleteWardrobeItem DB error:', e.message);
    throw e;
  }

  // 2. Supprimer l'image S3 via URL présignée (contourne le VPC)
  if (imageUrl && imageUrl.includes('outfit-photos-201064226116')) {
    try {
      // Extraire la clé S3
      const key = imageUrl.split('.amazonaws.com/').pop();
      // Demander une URL présignée DELETE au Lambda
      const res = await apiCall('POST', '/wardrobe/delete-image-url', { key }, token);
      if (res && res.deleteUrl) {
        // Appeler l'URL présignée directement depuis l'app (hors VPC)
        await fetch(res.deleteUrl, { method: 'DELETE' });
      }
    } catch(e) {
      // Non bloquant — l'article est déjà supprimé de la DB
      console.warn('S3 image delete failed (non bloquant):', e.message);
    }
  }
}

export async function uploadWardrobePhoto(userId, itemId, fileUri) {
  if (!IS_AWS_CONFIGURED) return null;
  try {
    const { uploadUrl, key } = await apiCall('POST', '/wardrobe/upload-url', {
      item_id: itemId, file_type: 'image/jpeg',
    });
    const blob = await fetch(fileUri).then(r => r.blob());
    await fetch(uploadUrl, { method: 'PUT', body: blob, headers: { 'Content-Type': 'image/jpeg' } });
    return `${AWS_CONFIG.cdnUrl}/${key}`;
  } catch { return null; }
}

export async function getLooks(userId) {
  if (!IS_AWS_CONFIGURED) return MOCK_LOOKS;
  try { return await apiCall('GET', '/looks') || MOCK_LOOKS; }
  catch { return MOCK_LOOKS; }
}

// v1.12.567 — Variante avec ETag : envoie If-None-Match, retourne { data, etag, status }
// Si status===304 → l'appelant doit utiliser son cache local
export async function getLooksWithEtag(token, etag) {
  if (!IS_AWS_CONFIGURED) return { data: MOCK_LOOKS, etag: null, status: 200 };
  const result = await apiCall('GET', '/looks', null, token, { ifNoneMatch: etag, returnMeta: true });
  if (result.status === 304) return result;
  return { data: result.data || [], etag: result.etag, status: result.status };
}

export async function saveLook(userId, look) {
  if (!IS_AWS_CONFIGURED) return { ...look, id: `look_${Date.now()}` };
  return look.id ? apiCall('PUT', `/looks/${look.id}`, look) : apiCall('POST', '/looks', look);
}

export async function deleteLook(lookId) {
  if (!IS_AWS_CONFIGURED) return;
  await apiCall('DELETE', `/looks/${lookId}`);
}

export async function getCatalog({ category, partner, search, available, limit = 50, offset = 0, sort, gender } = {}) {
  if (!IS_AWS_CONFIGURED) {
    let list = [...CATALOG];
    if (category && category !== 'Tout') list = list.filter(p => p.category === category);
    if (partner  && partner  !== 'Tout') list = list.filter(p => p.partner  === partner);
    if (available) list = list.filter(p => p.available);
    if (search)    list = list.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));
    if (sort === 'price_asc')  list.sort((a,b) => a.price - b.price);
    if (sort === 'price_desc') list.sort((a,b) => b.price - a.price);
    return list.slice(offset, offset + limit);
  }
  // ═══ FIX 2026-04 (v1.7.72) : utiliser URLSearchParams correctement ═══
  const qp = new URLSearchParams();
  qp.set('limit', String(limit));
  qp.set('offset', String(offset));
  if (category && category !== 'Tout') qp.set('category', category);
  if (partner  && partner  !== 'Tout') qp.set('partner',  partner);
  // ═══ v1.8.11 : passer gender pour filtrage côté Lambda ═══
  if (gender === 'M' || gender === 'F') qp.set('gender', gender);
  if (available) qp.set('available', 'true');
  if (search)    qp.set('search', search);
  if (sort)      qp.set('sort', sort);
  try {
    const data = await apiCall('GET', `/catalog?${qp.toString()}`);
    if (!data) return CATALOG;
    // ═══ v1.7.73 : mapper snake_case → camelCase pour affiliate_url ═══
    // ═══ v1.8.11 : map enrichi pour que les items DB (TOUS) soient ═══
    //   compatibles avec le format attendu partout dans l'app (image, etc.).
    return data.map(p => ({
      ...p,
      affiliateUrl: p.affiliate_url || p.affiliateUrl || null,
      image:        p.image || p.image_url || null,
      originalPrice: p.original_price != null ? parseFloat(p.original_price) : (p.originalPrice || null),
      price:        typeof p.price === 'number' ? p.price : parseFloat(p.price) || 0,
      reviewsCount: p.reviews_count != null ? p.reviews_count : (p.reviewsCount || 0),
      // gender depuis ai_metadata si présent (priorité), sinon depuis description.gender
      gender: p.ai_gender || p.gender || (p.description && typeof p.description === 'object' && p.description.gender) || null,
      // ai_metadata est exposé séparément pour les analyses IA (LooksScreen, etc.)
      ai_metadata: p.ai_metadata || null,
    }))
    // v1.12.977 — garde-fou : on n'affiche jamais un article sans image exploitable
    // (ex. articles Coutr sans visuel). Vaut pour catalogue + créer un look.
    .filter(hasUsableImage);
  } catch { return CATALOG; }
}

export async function getProductById(productId) {
  if (!IS_AWS_CONFIGURED) return CATALOG.find(p => p.id === productId) || null;
  return apiCall('GET', `/catalog/${productId}`);
}

export async function getCart(userId) {
  if (!IS_AWS_CONFIGURED) return [];
  try {
    const data = await apiCall('GET', '/cart');
    return (data || []).map(i => ({
      id: i.product_id, name: i.name, brand: i.brand, partner: i.partner,
      price: parseFloat(i.price), image: i.image_url, emoji: i.emoji,
      category: i.category, selectedSize: i.selected_size, color: i.color,
    }));
  } catch { return []; }
}

export async function addCartItem(userId, productId, size) {
  if (!IS_AWS_CONFIGURED) return null;
  return apiCall('POST', '/cart', { product_id: productId, selected_size: size || 'Unique' });
}

export async function removeCartItem(userId, productId, size) {
  if (!IS_AWS_CONFIGURED) return;
  await apiCall('DELETE', `/cart/${productId}?size=${encodeURIComponent(size || 'Unique')}`);
}

export async function clearCartDB(userId) {
  if (!IS_AWS_CONFIGURED) return;
  await apiCall('DELETE', '/cart/clear');
}

export async function createOrder(userId, { items, total, paymentMethod, deliveryAddress }) {
  if (!IS_AWS_CONFIGURED) {
    return { id: `ord_${Date.now()}`, status: 'confirmed', total, created_at: new Date().toISOString() };
  }
  return apiCall('POST', '/orders', {
    items, total, payment_method: paymentMethod || 'apple_pay',
    delivery_address: deliveryAddress || {},
  });
}

export async function getOrders(userId) {
  if (!IS_AWS_CONFIGURED) {
    const { mockOrders } = require('../data/deliveryData');
    return mockOrders;
  }
  try { return await apiCall('GET', '/orders') || []; }
  catch { return []; }
}

export async function getOrderById(orderId) {
  if (!IS_AWS_CONFIGURED) {
    const { mockOrders } = require('../data/deliveryData');
    return mockOrders.find(o => o.id === orderId) || null;
  }
  return apiCall('GET', `/orders/${orderId}`);
}

export async function getStockAlerts(userId) {
  if (!IS_AWS_CONFIGURED) {
    const { initialNotifications } = require('../data/stockData');
    return initialNotifications;
  }
  try { return await apiCall('GET', '/alerts') || []; }
  catch { return []; }
}

export async function addStockAlert(userId, productId, size, targetPrice) {
  if (!IS_AWS_CONFIGURED) return null;
  return apiCall('POST', '/alerts', { product_id: productId, target_size: size, target_price: targetPrice });
}

export async function removeStockAlert(userId, productId, size) {
  if (!IS_AWS_CONFIGURED) return;
  await apiCall('DELETE', `/alerts/${productId}`);
}

export async function getTrips(userId) {
  if (!IS_AWS_CONFIGURED) {
    try { return require('../data/travelData').initialTrips || []; } catch { return []; }
  }
  try { return await apiCall('GET', '/trips') || []; }
  catch { return []; }
}

export async function createTrip(userId, trip) {
  if (!IS_AWS_CONFIGURED) return { ...trip, id: `trip_${Date.now()}` };
  return apiCall('POST', '/trips', trip);
}

export async function getNotifications(userId) {
  if (!IS_AWS_CONFIGURED) {
    try { return require('../data/stockData').initialNotifications; } catch { return []; }
  }
  try { return await apiCall('GET', '/notifications') || []; }
  catch { return []; }
}

export async function markNotificationRead(notificationId) {
  if (!IS_AWS_CONFIGURED) return;
  await apiCall('PUT', `/notifications/${notificationId}/read`);
}

export async function markAllNotificationsRead(userId) {
  if (!IS_AWS_CONFIGURED) return;
  await apiCall('PUT', '/notifications/read-all');
}

export function subscribeToOrderUpdates(orderId, callback) {
  return { unsubscribe: () => {} }; // WebSocket à implémenter via API Gateway WS
}

export function subscribeToNotifications(userId, callback) {
  return { unsubscribe: () => {} };
}
